/**
 * Loading, empty and error states.
 *
 * Every screen implements all of them explicitly — a bare spinner on a blank
 * page tells the user nothing, and an empty table is indistinguishable from a
 * broken one.
 */
import type { ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

import type { ApiError } from '@/api/client';

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{ mb: 3, alignItems: { sm: 'flex-start' }, justifyContent: 'space-between' }}
    >
      <Box>
        <Typography variant="h1" component="h1">
          {title}
        </Typography>
        {description && (
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        )}
      </Box>
      {actions && <Stack direction="row" spacing={1}>{actions}</Stack>}
    </Stack>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <Stack spacing={1.5} sx={{ alignItems: 'center', textAlign: 'center', py: 8 }}>
      <InboxOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
      <Typography variant="h3" component="p">
        {title}
      </Typography>
      <Typography color="text.secondary">{description}</Typography>
      {action}
    </Stack>
  );
}

export function ErrorState({ error, onRetry }: { error: ApiError; onRetry?: () => void }) {
  // 503 is an outage, not a failure of the request — say so, because the user's
  // instinct otherwise is to sign out and back in, which cannot help.
  const isOutage = error.isOutage;
  return (
    <Alert
      severity={isOutage ? 'warning' : 'error'}
      action={
        onRetry && (
          <Button color="inherit" size="small" onClick={onRetry}>
            Erneut versuchen
          </Button>
        )
      }
      sx={{ my: 2 }}
    >
      <AlertTitle>
        {isOutage ? 'Ein benötigter Dienst ist nicht erreichbar' : 'Fehler'}
      </AlertTitle>
      {error.message}
    </Alert>
  );
}
